/**
 * Interpreting `GET esign/start-digio` (cpcore esign.service.ts `esignDoc`).
 *
 * Since the eSign active-record change (docs/esign-active-record), cpcore may answer with the
 * SAME unsigned agreement (`reused`), tell us the tenant already signed (`alreadySigned`), or
 * ask them to wait because another tap is creating it right now (`inProgress`, success:false).
 * `fetchData` returns the parsed body, or undefined on a swallowed non-2xx.
 */
/**
 * Refusal CODES start-digio can answer with (`{ success: false, message: <code>, data: {} }`) that
 * must never reach the screen raw. Every other message is already tenant copy and passes through.
 *
 * LENDER_REQUIRES_WITHOUT_BANKING (cpcore esign.service.ts `esignDoc`, owner ruling L14): this journey's
 * lender signs only on the no-bank-statement path and the journey is not on it. Nothing the
 * tenant can do fixes it, so the copy points them to support. Keep it in step with
 * src/b2b/lib/esignStartRefusal.js (the start-esign card shows the same refusal).
 *
 * DISBURSEMENT_DATE_PASSED (cpcore esign.service.ts `esignDoc`): the journey's disbursement date
 * is already behind us and nothing the tenant retries fixes it, so it is `blocked` (the screen
 * disables Next), not an `error`. Copy in ./disbursementDatePassed.js, naming the tenant's partner.
 */
import {
  DISBURSEMENT_DATE_PASSED,
  disbursementDatePassedMessage,
} from "./disbursementDatePassed.js";

export const START_DIGIO_REFUSAL_MESSAGES = Object.freeze({
  LENDER_REQUIRES_WITHOUT_BANKING:
    "We can't continue to signing right now. Please contact our support team and they will help you with the next step.",
});

function startDigioErrorMessage(message) {
  if (typeof message !== "string" || !message) return null;
  if (Object.prototype.hasOwnProperty.call(START_DIGIO_REFUSAL_MESSAGES, message)) {
    return START_DIGIO_REFUSAL_MESSAGES[message];
  }
  return message;
}

/** `partner` is the partner tag, for the `blocked` copy that names the tenant's partner team. */
export function interpretStartDigio(res, { partner } = {}) {
  if (!res || typeof res !== "object") return { kind: "error", message: null };
  if (res.alreadySigned === true) return { kind: "signed" };
  if (res.inProgress === true) return { kind: "wait", message: typeof res.message === "string" && res.message ? res.message : null };
  if (res.success === false && res.message === DISBURSEMENT_DATE_PASSED) {
    return { kind: "blocked", message: disbursementDatePassedMessage(partner) };
  }
  if (res.success === false || !res.session_url) {
    return { kind: "error", message: startDigioErrorMessage(res.message) };
  }
  return { kind: "redirect", url: res.session_url };
}

/**
 * Call start-digio, and while cpcore says another tap is still creating the agreement
 * (`inProgress`), wait and ask again — the next answer is normally the SAME agreement
 * (`reused`). Gives up after `maxAttempts` and returns the last interpretation.
 */
export async function startDigioWithRetry(fetchStart, { maxAttempts = 8, delayMs = 4000, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), partner } = {}) {
  let next = { kind: "error", message: null };
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    next = interpretStartDigio(await fetchStart(), { partner });
    if (next.kind !== "wait") return next;
    if (attempt < maxAttempts) await sleep(delayMs);
  }
  return next.kind === "wait" ? { kind: "error", message: next.message } : next;
}
